/**
 * Mobile State Restoration Hook
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Handles restoring form state from sessionStorage
 */

import { useEffect, RefObject } from "react";
import { useLocation } from "react-router-dom";
import { TransactionCategory, TransactionCategoryGroup, TransactionCategoryDetail, TransactionCustomFieldValues } from "../../../../types";

// Local types matching CreateTransactionMobile.tsx
interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: any;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

export type FocusedField = "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | `detail:${string}` | null;

export interface UseMobileStateRestorationProps {
  transactionType: "expense" | "income" | "transfer";
  isEditMode: boolean;
  loading: boolean;
  accounts: any[];
  categoryGroups: TransactionCategoryGroup[];
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  transferFormData: TransferFormData;
  setTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  setSelectedCategoryDetails: (details: TransactionCategoryDetail[]) => void;
  setCalculatorExpression: React.Dispatch<React.SetStateAction<string>>;
  setAddOptionDialogDetailName: React.Dispatch<React.SetStateAction<string>>;
  setAddOptionDialogValue: React.Dispatch<React.SetStateAction<string>>;
  setAddOptionDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setFocusedField: (field: FocusedField) => void;
  setNewlyAddedDetailOptions: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  setOpenDetailLists: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setRateManuallyEdited: React.Dispatch<React.SetStateAction<boolean>>;
  getSavedState: () => any;
  hasRestoredStateRef: RefObject<boolean>;
  location: ReturnType<typeof useLocation>;
}

export function useMobileStateRestoration({
  transactionType,
  isEditMode,
  loading,
  accounts,
  categoryGroups,
  expenseCategories,
  incomeCategories,
  formData,
  setFormData,
  transferFormData,
  setTransferFormData,
  setSelectedCategoryDetails,
  setCalculatorExpression,
  setAddOptionDialogDetailName,
  setAddOptionDialogValue,
  setAddOptionDialogOpen,
  setFocusedField,
  setNewlyAddedDetailOptions,
  setOpenDetailLists,
  setRateManuallyEdited,
  getSavedState,
  hasRestoredStateRef,
  location,
}: UseMobileStateRestorationProps) {
  // Reset restoration flag when component mounts or location changes to transaction page
  useEffect(() => {
    const isOnTransactionPage = location.pathname.includes('/create-transaction/');
    if (isOnTransactionPage) {
      // Reset flag when on transaction page to allow restoration on each mount
      hasRestoredStateRef.current = false;
    }
  }, [location.pathname, hasRestoredStateRef]);

  // Restore state from sessionStorage after data is loaded
  useEffect(() => {
    // Only restore after necessary data is loaded, and only once per mount
    // For expense/income: need category groups
    // For transfer: need accounts
    const hasRequiredData = transactionType === "transfer" 
      ? accounts.length > 0 
      : categoryGroups.length > 0;
    
    // CRITICAL: Never restore state in edit mode - we want to load the actual transaction data
    if (isEditMode) {
      return;
    }
    
    // Check if we have required data and haven't restored yet
    // If we have the required data, we can restore regardless of loading state
    // (loading might be true due to other async operations that don't affect restoration)
    if (hasRestoredStateRef.current) {
      return;
    }
    
    if (!hasRequiredData) {
      return;
    }
    
    // We have required data - proceed with restoration
    const savedState = getSavedState();
    if (!savedState) {
      return;
    }
    if (savedState.transactionType !== transactionType) {
      return;
    }
    
    hasRestoredStateRef.current = true; // Mark as restored
      // Restore form data
      if (transactionType === "transfer" && savedState.transferFormData) {
        // Reset time field when restoring - don't persist time between sessions
        setTransferFormData({ ...savedState.transferFormData, time: "" });
      } else if (savedState.formData) {
        // Reset time field when restoring - don't persist time between sessions
        // Ensure defaultCurrencyRate exists (for backward compatibility with old saved states)
        setFormData({ ...savedState.formData, time: "", defaultCurrencyRate: savedState.formData.defaultCurrencyRate || "" });
        
        // If category is restored, also restore category details
        if (savedState.formData?.category) {
          const categories = transactionType === "expense" ? expenseCategories : incomeCategories;
          const selectedCategory = categories.find(cat => cat.name === savedState.formData?.category);
          
          if (selectedCategory) {
            // Get details from the category's group
            const group = selectedCategory?.groupId ? categoryGroups.find((g) => g.id === selectedCategory.groupId) : null;
            const allGroupDetails = group?.details || [];
            
            // Only show details that are explicitly enabled for this category
            const enabledDetailNames = Array.isArray(selectedCategory?.enabledDetails) 
              ? selectedCategory.enabledDetails 
              : [];
            
            const details = enabledDetailNames.length > 0 
              ? allGroupDetails.filter((detail: TransactionCategoryDetail) => enabledDetailNames.includes(detail.name))
              : [];
            
            setSelectedCategoryDetails(details);
          } else if (savedState.selectedCategoryDetails) {
            // If category not found but we have saved details, restore them
            setSelectedCategoryDetails(savedState.selectedCategoryDetails);
          }
        } else if (savedState.selectedCategoryDetails) {
          // Restore details even if no category (edge case)
          setSelectedCategoryDetails(savedState.selectedCategoryDetails);
        }
      }
      
      // Restore other state
      if (savedState.calculatorExpression !== undefined) {
        setCalculatorExpression(savedState.calculatorExpression);
      }
      // Restore dialog state
      if (savedState.addOptionDialogDetailName !== undefined) {
        setAddOptionDialogDetailName(savedState.addOptionDialogDetailName);
      }
      if (savedState.addOptionDialogValue !== undefined) {
        setAddOptionDialogValue(savedState.addOptionDialogValue);
      }
      if (savedState.addOptionDialogOpen !== undefined && savedState.addOptionDialogOpen) {
        // Use setTimeout to ensure dialog can open properly after component is fully rendered
        setTimeout(() => {
          setAddOptionDialogOpen(true);
        }, 300);
      } else if (savedState.addOptionDialogOpen === false) {
        setAddOptionDialogOpen(false);
      }
      if (savedState.focusedField !== undefined && savedState.focusedField !== null && savedState.focusedField !== "time") {
        // Use setTimeout to ensure UI is ready before focusing
        // Don't restore time field focus - time should be reset each time
        setTimeout(() => {
          setFocusedField(savedState.focusedField as FocusedField);
        }, 150);
      }
      if (savedState.newlyAddedDetailOptions !== undefined) {
        setNewlyAddedDetailOptions(savedState.newlyAddedDetailOptions);
      }
      if (savedState.openDetailLists !== undefined) {
        setOpenDetailLists(savedState.openDetailLists);
      }
      if (savedState.rateManuallyEdited !== undefined) {
        setRateManuallyEdited(savedState.rateManuallyEdited);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, categoryGroups.length, accounts.length, expenseCategories.length, incomeCategories.length, transactionType, isEditMode, getSavedState, hasRestoredStateRef, setFormData, setTransferFormData, setSelectedCategoryDetails, setCalculatorExpression, setAddOptionDialogDetailName, setAddOptionDialogValue, setAddOptionDialogOpen, setFocusedField, setNewlyAddedDetailOptions, setOpenDetailLists, setRateManuallyEdited, categoryGroups, expenseCategories, incomeCategories]);
}
